import { useCallback } from 'react';

import type { EndpointsMap } from '@iconik-io/iconik-api/src/endpoints';
import { httpGetAclTemplatesByTemplateId } from '@iconik-io/iconik-api/src/services/acls';
import {
  httpGetAssetsByAssetId,
  httpGetAssetsByAssetIdRelationsByRelationType,
  httpGetAssetsByAssetIdSegmentsBySegmentId,
  httpGetAssetsRelationTypesByRelationType,
  httpGetByObjectTypeByObjectIdSharesByShareId,
  httpGetByObjectTypeByObjectIdSharesByShareIdUsersByShareUserId,
  httpGetCollectionsByCollectionId,
  httpGetCustomActionsByContextByActionId,
  httpGetProjectsByProjectId,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpGetAppsByAppId,
  httpGetAuthSamlIdpByIdentityProviderId,
  httpGetAuthTokenByTokenId,
  httpGetSystemDomainsBySystemDomainId,
} from '@iconik-io/iconik-api/src/services/auth';
import { httpGetAutomationsByAutomationId } from '@iconik-io/iconik-api/src/services/automations';
import {
  httpGetAssetsByAssetIdFileSetsByFileSetId,
  httpGetAssetsByAssetIdFilesByFileId,
  httpGetAssetsByAssetIdFormatsByFormatId,
  httpGetAssetsByAssetIdKeyframesByKeyframeId,
  httpGetAssetsByAssetIdProxiesByProxyId,
  httpGetCollectionsByCollectionIdKeyframesByKeyframeId,
  httpGetExportLocationsByExportLocationId,
  httpGetStorageGatewayClustersByClusterId,
  httpGetStorageGatewaysByStorageGatewayId,
  httpGetStoragesByStorageId,
  httpGetTranscodersByTranscoderId,
} from '@iconik-io/iconik-api/src/services/files';
import { httpGetJobsByJobId } from '@iconik-io/iconik-api/src/services/jobs';
import {
  httpGetFieldsByFieldName,
  httpGetViewsByViewId,
} from '@iconik-io/iconik-api/src/services/metadata';
import { httpGetFaceRecognitionPersonsByPersonId } from '@iconik-io/iconik-api/src/services/ml';
import { httpGetWebhooksByWebhookId } from '@iconik-io/iconik-api/src/services/notifications';
import {
  httpGetDiscoveryDefaultEntitiesByEntityId,
  httpGetSearchSavedBySearchId,
  httpGetViewsByViewId as httpGetSearchViewsByViewId,
} from '@iconik-io/iconik-api/src/services/search';
import { httpGetSystemLogsRecipientsByLogsRecipientId } from '@iconik-io/iconik-api/src/services/stats';
import {
  httpGetGroupsByGroupId,
  httpGetRoleGroupsByGroupId,
  httpGetTeamsByTeamId,
  httpGetUsersByUserId,
} from '@iconik-io/iconik-api/src/services/users';

import type { AppDispatch } from '../../../redux/types';
import { useEntity } from './use_entity';

const httpGetStorageGatewayClustersByClusterIdWithView = (clusterId: string) =>
  httpGetStorageGatewayClustersByClusterId(clusterId, {
    view_nodes: true,
    view_stats: true,
  });

const entityEndpointsMap = {
  assets: httpGetAssetsByAssetId,
  segments: httpGetAssetsByAssetIdSegmentsBySegmentId,
  acl_templates: httpGetAclTemplatesByTemplateId,
  application_tokens: httpGetAuthTokenByTokenId,
  applications: httpGetAppsByAppId,
  asset_persons: httpGetFaceRecognitionPersonsByPersonId,
  asset_relations: httpGetAssetsByAssetIdRelationsByRelationType,
  automations: httpGetAutomationsByAutomationId,
  collection_keyframes: httpGetCollectionsByCollectionIdKeyframesByKeyframeId,
  collections: httpGetCollectionsByCollectionId,
  custom_actions: httpGetCustomActionsByContextByActionId,
  discoveries: httpGetDiscoveryDefaultEntitiesByEntityId,
  discoveries_admin: httpGetDiscoveryDefaultEntitiesByEntityId,
  export_locations: httpGetExportLocationsByExportLocationId,
  file_sets: httpGetAssetsByAssetIdFileSetsByFileSetId,
  files: httpGetAssetsByAssetIdFilesByFileId,
  formats: httpGetAssetsByAssetIdFormatsByFormatId,
  groups: httpGetGroupsByGroupId,
  identity_providers: httpGetAuthSamlIdpByIdentityProviderId,
  jobs: httpGetJobsByJobId,
  keyframes: httpGetAssetsByAssetIdKeyframesByKeyframeId,
  metadata_fields: httpGetFieldsByFieldName,
  metadata_unmerged_views: httpGetViewsByViewId,
  metadata_user_fields: httpGetFieldsByFieldName,
  metadata_views: httpGetViewsByViewId,
  projects: httpGetProjectsByProjectId,
  proxies: httpGetAssetsByAssetIdProxiesByProxyId,
  relation_types: httpGetAssetsRelationTypesByRelationType,
  role_groups: httpGetRoleGroupsByGroupId,
  saved_searches: httpGetSearchSavedBySearchId,
  search_views: httpGetSearchViewsByViewId,
  share_users: httpGetByObjectTypeByObjectIdSharesByShareIdUsersByShareUserId,
  shares: httpGetByObjectTypeByObjectIdSharesByShareId,
  storage_gateways: httpGetStorageGatewaysByStorageGatewayId,
  storage_gateway_clusters: httpGetStorageGatewayClustersByClusterIdWithView,
  storages: httpGetStoragesByStorageId,
  system_domains: httpGetSystemDomainsBySystemDomainId,
  system_logs_recipients: httpGetSystemLogsRecipientsByLogsRecipientId,
  teams: httpGetTeamsByTeamId,
  transcoders: httpGetTranscodersByTranscoderId,
  users: httpGetUsersByUserId,
  webhooks: httpGetWebhooksByWebhookId,
};

type SupportedEntityTypes = keyof typeof entityEndpointsMap;

type EndpointResults = {
  [key in keyof typeof entityEndpointsMap]: ReturnType<
    (typeof entityEndpointsMap)[key]
  >;
};

type EndpointId<EntityType extends SupportedEntityTypes> =
  EndpointResults[EntityType]['id'];

type OptionalTuple<T extends any[]> = {
  [K in keyof T]: T[K] | undefined;
};

function allParamsSet(params: (string | undefined)[]): params is string[] {
  return params.every((param) => param !== undefined);
}

export const useEntitySimple = <EntityType extends SupportedEntityTypes>(
  entityType: EntityType,
  ...params: OptionalTuple<EndpointsMap[EndpointId<EntityType>]['params']>
) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: params is an array of IDs
  const getItem = useCallback(
    (dispatch: AppDispatch) => {
      if (!allParamsSet(params)) {
        throw new Error('Parameters must be set to run getItem function');
      }
      // Tricky to properly type this, endpoint ends up with an intersection of all possible endpoints
      // and params itself is not detected as a tuple.
      // biome-ignore lint/plugin: see the comment above, the endpoint resolves to an intersection of every entity endpoint.
      const endpoint = entityEndpointsMap[entityType] as any;
      return dispatch(endpoint(...params));
    },
    [entityType, JSON.stringify(params)],
  );

  const isAllParamsSet = allParamsSet(params);

  return useEntity({
    entityType,
    primaryId: isAllParamsSet ? params.at(-1) : undefined,
    getItem,
  });
};
